import React from 'react';
import { Box, Typography, Container, Grid, Paper, Divider } from '@mui/material';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import WebIcon from '@mui/icons-material/Web';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const FeatureItem = ({ icon, title, content, index }) => {
  const { isDarkMode } = useTheme();
  
  return (
    <Grid item xs={12} md={6}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 * index }}
      >
        <Paper
          elevation={2}
          sx={{
            p: 4,
            height: '100%',
            borderRadius: 2,
            transition: 'all 0.3s ease',
            backgroundColor: isDarkMode ? 'background.paper' : 'background.paper',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
              backgroundColor: isDarkMode 
                ? 'rgba(46, 91, 255, 0.05)' 
                : 'rgba(46, 91, 255, 0.02)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <Box
              sx={{
                mr: 2,
                width: 50,
                height: 50,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #2E5BFF 0%, #00C896 100%)',
                color: 'white',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
            <Typography variant="h5" component="h3" gutterBottom fontWeight={600}>
              {title}
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {content}
          </Typography>
        </Paper>
      </motion.div>
    </Grid>
  );
};

const Features = () => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  
  const features = [
    { 
      icon: <StorageIcon fontSize="medium" />, 
      title: t('feature1Title'), 
      content: t('feature1Content')
    },
    { 
      icon: <CodeIcon fontSize="medium" />, 
      title: t('feature2Title'), 
      content: t('feature2Content')
    },
    { 
      icon: <SecurityIcon fontSize="medium" />, 
      title: t('feature3Title'), 
      content: t('feature3Content')
    },
    { 
      icon: <SpeedIcon fontSize="medium" />, 
      title: t('feature4Title'), 
      content: t('feature4Content')
    },
    { 
      icon: <WebIcon fontSize="medium" />, 
      title: t('feature5Title'), 
      content: t('feature5Content')
    },
    { 
      icon: <SupportAgentIcon fontSize="medium" />, 
      title: t('feature6Title'), 
      content: t('feature6Content')
    },
  ];

  return (
    <Box
      id="features"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: theme => 
          isDarkMode 
            ? theme.palette.background.default
            : 'rgba(245, 247, 250, 0.5)',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h2"
            component="h2"
            align="center"
            gutterBottom
            sx={{
              mb: 6,
              fontWeight: 700,
              background: 'linear-gradient(45deg, #2E5BFF, #00C896)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('featuresTitle')}
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              icon={feature.icon}
              title={feature.title}
              content={feature.content}
              index={index}
            />
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Features;
